import * as Sentry from '@sentry/vue';

export default defineNuxtPlugin((nuxtApp) => {
  const sentryDsn = useRuntimeConfig().public.sentryDsn;

  if (!sentryDsn) {
    return;
  }

  Sentry.init({
    app: nuxtApp.vueApp,
    // autoSessionTracking: true,
    dsn: sentryDsn,
    transport: Sentry.makeBrowserOfflineTransport(Sentry.makeFetchTransport),
    // debug: true,
    sendDefaultPii: true, // Enable IP address collection and other PII for debugging
    integrations: [
      Sentry.browserTracingIntegration({
        router: nuxtApp.$router,
        // tracePropagationTargets: [config.public.SENTRY_TRACE_PROPAGATION_TARGET],
      }),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
        maskAllInputs: false,
        useCompression: false,
        networkDetailAllowUrls: [
          useRequestURL().origin,
          /^\/api\//,
        ],
      }),
    ],
    trackComponents: true,
    hooks: ['activate', 'create', 'destroy', 'mount', 'update'],
    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: 1.0, // Increased to 100% for maximum debugging data
    ignoreErrors: ['ResizeObserver loop limit exceeded', 'Connection closed', 'Connection to server unavailable'],

    replaysSessionSampleRate: 1,
    replaysOnErrorSampleRate: 1,

    // Additional debugging options
    attachStacktrace: true, // Always attach stack traces
    includeLocalVariables: true, // Include local variables in stack traces
    normalizeDepth: 5, // Reduced from 10 to prevent size issues
    maxBreadcrumbs: 50, // Reduced from 100 to prevent size issues
    maxEventSize: 1000000, // 1MB limit to prevent envelope size errors

    mutationLimit: 8000,
    mutationBreadcrumbLimit: 600,

    transportOptions: {
      flushAtStartup: true,
    },

    // Size optimization
    beforeSend: (event) => {
      // Ignore Turnstile timeout errors (110600) - these are normal occurrences
      // The widget automatically resets when challenge times out
      if (event.exception?.values?.[0]?.value?.includes('[Cloudflare Turnstile] Error: 110600')) {
        return null; // Ignore this error
      }

      // Truncate large strings to prevent size issues
      if (event.message && event.message.length > 1000) {
        event.message = event.message.substring(0, 1000) + '... [truncated]';
      }

      // Limit extra data size
      if (event.extra && Object.keys(event.extra).length > 20) {
        const keys = Object.keys(event.extra).slice(0, 20);
        event.extra = keys.reduce((acc, key) => {
          acc[key] = event.extra[key];
          return acc;
        }, {});
      }

      return event;
    },
  });

  return {
    provide: {
      sentry: Sentry,
      replay: Sentry.getClient().getIntegrationByName('Replay'),
    },
  };
});
